import { donation_checkout_methods } from "./donation_campaigns_checkout_helper";

function get_completed_donations(campaign) {
  if (!Array.isArray(campaign?.donations)) {
    return [];
  }

  return campaign.donations.filter(
    (donation) => String(donation?.donation_status || "").toLowerCase() === "completed"
  );
}

export const get_campaign_summary = (description) => {
  const text = String(description || "").trim();
  if (!text) return "No campaign description yet.";
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
};

export const get_campaign_raised_amount = (campaign) => {
  const completed_donations = get_completed_donations(campaign);

  if (!completed_donations.length) {
    return Number(campaign?.amount_raised || campaign?.current_amount || 0);
  }

  return completed_donations.reduce(
    (total, donation) => total + Number(donation?.amount || 0),
    0
  );
};

export const get_progress = (campaign) => {
  const goal = Number(campaign?.goal_amount || 0);
  const raised = get_campaign_raised_amount(campaign);
  if (!goal) return 0;
  return Math.round((raised / goal) * 100);
};

export const get_progress_width = (campaign) => {
  return Math.min(100, get_progress(campaign));
};

export const format_currency = (amount) => {
  const value = Number(amount || 0);
  return `R${value.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

export const format_date = (value) => {
  if (!value) return "Date not set";
  return new Date(value).toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const get_donation_amount_value = function get_donation_amount_value() {
  return Number(this.donation_form.amount || 0);
};

export const is_long_campaign_description = function is_long_campaign_description(description) {
  return String(description || "").trim().length > 80;
};

export const format_datetime = function format_datetime(value) {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleString("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const calculate_days_left = function calculate_days_left(end_date) {
  if (!end_date) return 0;
  const today = new Date();
  const end = new Date(end_date);
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

function get_file_name_value(file = {}) {
  return String(file?.name || file?.file?.name || "").toLowerCase();
}

function get_file_url_value(file = {}) {
  return String(file?.file?.file_url || file?.file_url || file?.image_url || "").toLowerCase();
}

function get_file_match_value(file = {}) {
  return `${get_file_name_value(file)} ${get_file_url_value(file)}`;
}

const image_extensions = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp", ".avif"];
const video_extensions = [".mp4", ".mov", ".avi", ".webm", ".mkv", ".m4v"];

export function is_campaign_image_file(file = {}) {
  const value = get_file_match_value(file);
  return image_extensions.some((extension) => value.includes(extension));
}

export function is_campaign_video_file(file = {}) {
  const value = get_file_match_value(file);
  return video_extensions.some((extension) => value.includes(extension));
}

export function get_campaign_file_url(file = {}) {
  return file?.file?.file_url || file?.file_url || file?.image_url || "";
}


export function get_campaign_file_name(file = {}) {
  return file?.name || file?.file?.name || "";
}

export const get_campaign_cover_image = (campaign = {}) => {
  if (campaign?.cover_image) {
    return campaign.cover_image;
  }

  if (campaign?.image_url) {
    return campaign.image_url;
  }

  const files = Array.isArray(campaign?.files) ? campaign.files : [];
  const image_file = files.find((file) => is_campaign_image_file(file));
  return get_campaign_file_url(image_file);
};

function should_include_supporting_file(file, cover_image) {
  const file_url = get_campaign_file_url(file);
  if (!file_url) {
    return false;
  }

  if (cover_image && file_url === cover_image) {
    return false;
  }

  return true;
}

export function get_campaign_supporting_files(campaign = {}) {
  const supporting_documents = Array.isArray(campaign?.supporting_documents)
    ? campaign.supporting_documents
    : [];

  if (supporting_documents.length) {
    return supporting_documents;
  }

  const files = Array.isArray(campaign?.files) ? campaign.files : [];
  const cover_image = get_campaign_cover_image(campaign);

  return files.filter((file) => should_include_supporting_file(file, cover_image));
}

export function get_campaign_file_created_at(file = {}) {
  return file?.created_at || "";
}

export function get_campaign_supporting_files_count(campaign = {}) {
  return get_campaign_supporting_files(campaign).length;
}

function is_pdf_file(value) {
  return value.includes(".pdf");
}

function is_doc_file(value) {
  return value.includes(".doc") || value.includes(".docx");
}

function is_sheet_file(value) {
  const sheet_extensions = [".xls", ".xlsx", ".xlsm", ".xlsb", ".csv"];
  return sheet_extensions.some((extension) => value.includes(extension));
}

export function get_campaign_file_icon_class(file = {}) {
  const value = get_file_match_value(file);

  if (is_pdf_file(value)) {
    return "bi-file-earmark-pdf-fill text-danger";
  }

  if (is_doc_file(value)) {
    return "bi-file-earmark-word-fill text-primary";
  }

  if (is_sheet_file(value)) {
    return "bi-file-earmark-excel-fill text-success";
  }

  return "bi-file-earmark-text-fill text-secondary";
}

export const donation_form_methods = {
  reset_donation_form() {
    this.donation_form = {
      amount: "",
      message: "",
      is_anonymous: false,
    };
    this.donation_errors = {
      amount: "",
    };
  },
  select_donation_amount(amount) {
    this.donation_form.amount = String(amount || "");
  },
  get_donation_request_payload() {
    return {
      campaign_id: this.selected_donation_campaign?.id,
      amount: this.get_donation_amount_value(),
      message: String(this.donation_form.message || "").trim(),
      is_anonymous: this.donation_form.is_anonymous,
    };
  },
  append_payfast_form_inputs(data_array) {
    const form = $("#payment_form");
    form.find("input.dynamic-payfast-input").remove();

    Object.entries(data_array || {}).forEach(([key, value]) => {
      const input = $("#input_clone").clone();
      input.attr("name", key);
      input.attr("value", value);
      input.removeAttr("id");
      input.removeClass("d-none");
      input.addClass("dynamic-payfast-input");
      form.append($(input));
    });

    return form;
  },
  validate_donation_checkout_amount() {
    this.donation_errors.amount = "";
    const donation_amount = this.get_donation_amount_value();

    if (!donation_amount || donation_amount <= 0) {
      this.donation_errors.amount = "Enter a valid donation amount.";
      return false;
    }

    if (donation_amount < 5) {
      this.donation_errors.amount = "Donation amount must be at least R5.00.";
      return false;
    }

    return true;
  },
};

export { donation_checkout_methods };
